import React, { Component, ContextType, createRef } from 'react';
import { IApiUserTradingAccountType } from '@api/interfaces/data-contracts';
import { apiMasterAccount } from '@api/services/masterAccount.apiService';
import { apiUserCopyTrading } from '@api/services/user/copyTrading/userCopyTrading.apiService';
import { WarningMessage } from '@components-library/WarningMessage/WarningMessage';
import { AccountCreateButtons } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateButtons/AccountCreateButtons';
import { BeforeActionsContent } from '@components-member/TradingAccounts/AccountCreate/components/BeforeActionContent/BeforeActionContent';
import {
  IMasterAccountCreateRequest,
  IMasterAccountCreateState,
} from '@components-member/TradingAccounts/AccountCreate/components/Forms/Master/interfaces';
import { CREATE_NEW_OPTION_VALUE } from '@components-member/TradingAccounts/AccountCreate/constants';
import { AccountCreateContext } from '@components-member/TradingAccounts/AccountCreate/context';
import {
  EnumUTAFormTAField,
  ACCOUNT_CREATE_ACTION_TYPE_ENUM,
} from '@components-member/TradingAccounts/AccountCreate/enums';
import {
  changeLeverage,
  getAccountType,
  setIbCodeTooltip,
} from '@components-member/TradingAccounts/AccountCreate/helpers';
import { IBaseAccountCreateWithContext } from '@components-member/TradingAccounts/AccountCreate/interfaces';
import { getSocialTradingTypeOption } from '@components-member/TradingAccounts/AccountCreate/socialTrading/helpers';
import { configureAccountSettings } from '@components-member/socialTrading/shared/accountSettingsService';
import { createMasterAccount, getCopyTradingSettings } from '@components-member/socialTrading/shared/requests';
import { IModalFormField } from '@components-member/socialTrading/social.interfaces';
import {
  copyTypeOptionsHandler,
  filterIbCodeFieldIfDisabled,
  getGroupMappingTypeById,
  getMasterRecommendedDepositTooltipMsg,
  isMasterDepositFieldsVisible,
  setTooltipMessage,
} from '@components-member/socialTrading/utils';
import CONSTANTS from '@components-shared/constants';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { pushAccountCreateErrorEvent } from '@components-shared/googleAnalyticsEvents';
import { NotificationDialog } from '@components-shared/modal';
import Preloader from '@components-shared/preloader';
import { EnumUSTFormField } from '@components-shared/socialTrading/enums';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getInitialState } from 'components/shared/forms/formGenerator';
import { connectToContext } from 'contextStore';
import { TRADING_ACCOUNT_SOURCE } from 'global.enums';
import tradingAccountFormStyles from '../tradingAccountForm.styles.module.scss';
import { MASTER_ACCOUNT_FIELDS, MasterAccountForm } from './constants';

class MasterAccountCreate extends Component<IBaseAccountCreateWithContext, IMasterAccountCreateState> {
  static contextType = AccountCreateContext;

  // @ts-ignore declare cannot be used at the current moment
  context!: ContextType<typeof AccountCreateContext>;

  MASTER_FIELDS = [...MASTER_ACCOUNT_FIELDS];

  formRef = createRef<{
    update: (data: IModalFormField[]) => void;
    validate: () => boolean;
    resetValidation: () => void;
  }>();

  state: IMasterAccountCreateState = {
    processing: true,
    isCreatingTradingAccount: false,
    form: { ...getInitialState(this.MASTER_FIELDS) },
    agree: false,
    profitSharingLimit: 0,
    recommendedMinDepositLimits: [],
    selectedAccountDescription: null,
  };

  async componentDidMount() {
    await this.getMasterAccountInitialData();
  }

  async componentDidUpdate(
    _prevProps: Readonly<IBaseAccountCreateWithContext>,
    prevState: Readonly<IMasterAccountCreateState>,
  ) {
    if (prevState.form.type !== this.state.form.type) {
      await this.handleAccountSettings(this.state.form.type);
    }
  }

  getMasterAccountInitialData = async (): Promise<void> => {
    const { updateActionState, formOpeningSource } = this.context;

    try {
      const getMasterTypes = async () => {
        if (formOpeningSource !== TRADING_ACCOUNT_SOURCE.SIDEBAR) {
          return { data: null };
        }
        return await apiMasterAccount.getInitialDataList();
      };

      const [{ data: masterTypes }, socialTradingSettings] = await Promise.all([
        getMasterTypes(),
        getCopyTradingSettings(),
      ]);

      const { profitSharingLimit, recommendedMinDepositLimits } = socialTradingSettings.data;

      if (masterTypes) {
        updateActionState({
          type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.MASTER_TRADING_TYPES,
          payload: masterTypes,
        });
      }

      this.setState(
        {
          processing: false,
          profitSharingLimit,
          recommendedMinDepositLimits,
        },
        this.formInitializer,
      );
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  formInitializer = (): void => {
    const { canSetIb, clientSourceType, ibCodePrefix } = this.props;
    const { masterTrading } = this.context.accountCreateState;
    const typeIdx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUTAFormTAField.type);

    getSocialTradingTypeOption(masterTrading, this.MASTER_FIELDS, this.context.selectedPlatformType);
    setIbCodeTooltip({ fields: this.MASTER_FIELDS, canSetIb, clientSourceType, ibCodePrefix });
    this.MASTER_FIELDS = filterIbCodeFieldIfDisabled(this.MASTER_FIELDS, canSetIb, clientSourceType);

    this.setProfitSharingLimit();

    const isLimit = masterTrading.length === 0;

    FormUtils.updateFormField(typeIdx, { attributes: { disabled: isLimit } }, this.MASTER_FIELDS);

    this.updateForm();
  };

  setProfitSharingLimit = (): void => {
    const { profitSharingLimit } = this.state;

    const idx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUTAFormTAField.profitSharing);

    if (idx > -1) {
      this.MASTER_FIELDS[idx].maxValue = profitSharingLimit;
    }
  };

  handleAccountSettings = async (value: number) => {
    if (!this.formRef.current) {
      return;
    }

    const { availableAllocationMethods } = await configureAccountSettings({
      value,
      fields: this.MASTER_FIELDS,
      form: this.formRef,
      onChange: this.onChange,
      fetchSettings: () => apiUserCopyTrading.getMasterSettings({ tradingAccountTypeId: value }),
    });

    copyTypeOptionsHandler(availableAllocationMethods, this.MASTER_FIELDS);
  };

  typeTriggerHandler = (value: number): void => {
    const { form } = this.state;
    const { masterTrading } = this.context.accountCreateState;
    const leverageIdx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUTAFormTAField.leverage);
    const depositAmountIdx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUSTFormField.depositAmount);
    const depositAmountRequiredIdx = FormUtils.getFieldIndex(
      this.MASTER_FIELDS,
      EnumUSTFormField.makeMinDepositAmountRequired,
    );
    const commissionAccountIdIdx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUSTFormField.commissionAccountId);

    const accountType = getAccountType(masterTrading, value);

    this.setState({
      selectedAccountDescription: accountType?.description ?? null,
    });

    if (!accountType) {
      this.disableRecommendedDepositFieldToggle(depositAmountIdx, depositAmountRequiredIdx);

      return;
    }

    this.onChange('leverage', '');
    this.onChange('commissionAccountId', '');

    changeLeverage(leverageIdx, accountType, this.MASTER_FIELDS);

    this.setCommissionAccountOptions(commissionAccountIdIdx, accountType);

    this.enableRecommendedDepositFieldToggle(
      depositAmountIdx,
      depositAmountRequiredIdx,
      accountType,
      form.makeMinDepositAmountRequired,
    );
  };

  enableRecommendedDepositFieldToggle = (
    depositAmountFieldIdx: number,
    depositAmountRequiredIdx: number,
    accountType: IApiUserTradingAccountType,
    makeMinDepositAmountRequired: boolean,
  ): void => {
    const groupMappingType = getGroupMappingTypeById(accountType);
    const { recommendedMinDepositLimits } = this.state;

    const isVisible = isMasterDepositFieldsVisible(recommendedMinDepositLimits, groupMappingType);

    if (isVisible) {
      FormUtils.updateFormField(depositAmountFieldIdx, { isHidden: false }, this.MASTER_FIELDS);
      FormUtils.updateFormField(depositAmountRequiredIdx, { isHidden: false }, this.MASTER_FIELDS);

      this.onDepositRequiredChange(makeMinDepositAmountRequired, accountType, depositAmountFieldIdx, false);

      return;
    }
  };

  disableRecommendedDepositFieldToggle = (depositAmountFieldIdx: number, depositAmountRequiredIdx: number): void => {
    this.clearRecommendedDepositValue();

    FormUtils.updateFormField(depositAmountFieldIdx, { isHidden: true, required: false }, this.MASTER_FIELDS);
    FormUtils.updateFormField(depositAmountRequiredIdx, { isHidden: true }, this.MASTER_FIELDS);

    this.formRef.current?.update(this.MASTER_FIELDS);

    return;
  };

  setDepositAmountTooltip = (accountType: IApiUserTradingAccountType): void | null => {
    const { recommendedMinDepositLimits, form } = this.state;

    if (!accountType) {
      return null;
    }

    const accountGroupMappingType = getGroupMappingTypeById(accountType);

    const tooltipMessage = getMasterRecommendedDepositTooltipMsg(
      recommendedMinDepositLimits,
      accountType.currency,
      form.makeMinDepositAmountRequired,
      accountGroupMappingType,
    );

    setTooltipMessage(
      FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUSTFormField.depositAmount),
      tooltipMessage,
      this.MASTER_FIELDS,
    );
  };

  clearRecommendedDepositValue = (): void => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        recommendedMinDepositAmount: Number(null),
      },
    }));
  };

  makeDepositReqTriggerHandler = (value: boolean): void => {
    const { form } = this.state;
    const { masterTrading } = this.context.accountCreateState;
    const accountType = getAccountType(masterTrading, form.type);

    if (!accountType) {
      return;
    }

    const depositAmountIdx = FormUtils.getFieldIndex(this.MASTER_FIELDS, EnumUSTFormField.depositAmount);

    this.onDepositRequiredChange(value, accountType, depositAmountIdx, true);
  };

  onDepositRequiredChange = (
    isRequired: boolean,
    accountType: IApiUserTradingAccountType,
    depositAmountFieldIdx: number,
    isDepositReqTrigger: boolean,
  ): void => {
    FormUtils.updateFormField(depositAmountFieldIdx, { required: isRequired }, this.MASTER_FIELDS);
    this.setDepositAmountTooltip(accountType);

    if (isDepositReqTrigger) {
      this.resetFormValidation();
    }
  };

  setCommissionAccountOptions = (commissionAccountIdIdx: number, accountType: IApiUserTradingAccountType): void => {
    const { i18next } = this.props;
    const { commissionAccounts } = accountType;

    const options = commissionAccounts.map((acc) => ({ name: acc.login, value: acc.id }));

    options.unshift({ name: i18next.t(keyMap.formsCreateNew), value: CREATE_NEW_OPTION_VALUE });

    FormUtils.updateFormField(commissionAccountIdIdx, { options }, this.MASTER_FIELDS);
  };

  updateForm = (): void => {
    if (this.formRef && this.formRef.current) {
      this.formRef.current.update(this.MASTER_FIELDS);
    }
  };

  isValidForm = (): boolean | undefined => {
    if (this.formRef && this.formRef.current) {
      return this.formRef.current.validate();
    }
  };

  resetFormValidation = (): void => {
    if (this.formRef && this.formRef.current) {
      this.formRef.current.resetValidation();
    }
  };

  changeAgree = (): void => {
    const { agree } = this.state;

    this.setState({ agree: !agree });
  };

  onChange = (name: string, value: unknown): void => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: value,
      },
    }));
  };

  renderBeforeActionContent = () => {
    const { selectedAccountDescription } = this.state;

    return (
      <BeforeActionsContent
        changeAgree={this.changeAgree}
        agree={this.state.agree}
        innerFooterChildren={
          selectedAccountDescription && (
            <WarningMessage
              className={tradingAccountFormStyles.noteMessageInner}
              title={keyMap.commonPleaseNote}
              type="warning">
              {selectedAccountDescription}
            </WarningMessage>
          )
        }
      />
    );
  };

  createAccount = async (): Promise<void> => {
    const { refresh } = this.context;
    const isPreloaderEnable = true;

    const isValid = this.isValidForm();

    if (!isValid) {
      return;
    }

    try {
      this.setState({ isCreatingTradingAccount: true });
      const formData = this.apiCallFormData(this.state.form);

      await createMasterAccount(formData, this.props.user);

      NotificationDialog.show(keyMap.tradingAccountsAccountSuccessfullyCreated);
      this.setState({ isCreatingTradingAccount: false });
      this.props.closeHandler();

      refresh({ isPreloaderEnable });
    } catch (error) {
      this.setState({ isCreatingTradingAccount: false });
      pushAccountCreateErrorEvent(error.response.data);
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  apiCallFormData = (form: IMasterAccountCreateRequest): IMasterAccountCreateRequest => {
    const formData = { ...form };

    const { commissionAccountId } = formData;

    if (
      commissionAccountId === CONSTANTS.DEFAULT_SELECT_OPTION_VALUE ||
      commissionAccountId === CREATE_NEW_OPTION_VALUE
    ) {
      formData.commissionAccountId = null;
      return formData;
    }

    return formData;
  };

  render() {
    const { isAccountLimitForUnverifiedUsers } = this.context;
    const { processing, form, agree, isCreatingTradingAccount } = this.state;
    const { changeStep, isBackButtonDisplayed } = this.props;

    if (processing) {
      return <Preloader />;
    }

    const disabled = !agree || isAccountLimitForUnverifiedUsers || isCreatingTradingAccount;

    return (
      <MasterAccountForm
        className="grid--form"
        form={form}
        ref={this.formRef}
        onChange={this.onChange}
        typeTrigger={this.typeTriggerHandler}
        makeDepositReqTrigger={this.makeDepositReqTriggerHandler}
        beforeActions={this.renderBeforeActionContent()}
        after={
          <AccountCreateButtons
            isCreatingTradingAccount={isCreatingTradingAccount}
            createAction={this.createAccount}
            disabled={disabled}
            changeStepHandler={changeStep}
            isBackButtonDisplayed={isBackButtonDisplayed}
          />
        }
      />
    );
  }
}

const MasterAccountCreateWithContext = connectToContext(MasterAccountCreate);

export { MasterAccountCreateWithContext as MasterAccountCreate };
